import { addToast, type ToastProps } from "@heroui/react";
import type { ReactNode } from "react";

/**
 * HeroUI Toast Helper Functions
 * 
 * These functions provide a drop-in replacement for react-toastify's toast API
 * while using HeroUI's addToast under the hood.
 * 
 * @example
 * // Simple usage
 * toast.success("Operation successful");
 * toast.error("An error occurred");
 * toast.info("Here is some information");
 * toast.warning("Warning message");
 * 
 * // With custom title and message
 * toast("Custom message", { type: "success" });
 * 
 * // With custom component
 * toast(<CustomComponent />, { type: "success" });
 * 
 * // With all options
 * toast("Message", {
 *   type: "success",
 *   title: "Custom Title",
 *   autoClose: 5000,
 *   hideCloseButton: false,
 *   hideIcon: false,
 * });
 */

// Type definitions for compatibility with react-toastify
type ToastType = "success" | "error" | "info" | "warning" | "default";

interface ToastOptions {
    type?: ToastType;
    title?: string;
    autoClose?: number | false;
    hideCloseButton?: boolean;
    hideIcon?: boolean;
    closeButton?: boolean;
    icon?: ReactNode;
    position?: "top-left" | "top-right" | "top-center" | "bottom-left" | "bottom-right" | "bottom-center";
}

/**
 * Maps react-toastify types to HeroUI severity/color
 */
const mapTypeToSeverity = (type: ToastType): ToastProps["severity"] => {
    const mapping: Record<ToastType, ToastProps["severity"]> = {
        success: "success",
        error: "danger",
        info: "primary",
        warning: "warning",
        default: "default",
    };
    return mapping[type];
};

/**
 * Main toast function - compatible with react-toastify API
 * 
 * @example
 * toast("Transaction successful", { type: "success" });
 * toast(<CustomComponent />, { type: "error", title: "Error" });
 */
export function toast(
    content: ReactNode | string,
    options: ToastOptions = {}
): void {
    const {
        type = "default",
        title,
        autoClose = 6000,
        hideCloseButton = false,
        hideIcon = false,
        icon,
        position = "top-right",
    } = options;

    // Determine if content should be title or description
    const isSimpleString = typeof content === "string" && !title;
  
    addToast({
        title: isSimpleString ? content as string : title,
        description: isSimpleString ? undefined : content,
        severity: mapTypeToSeverity(type),
        timeout: autoClose === false ? 0 : (autoClose as number),
        hideCloseButton,
        hideIcon,
        icon,
    });
}

/**
 * Success toast - shorthand for toast with type "success"
 * 
 * @example
 * toast.success("Operation completed successfully");
 * toast.success("Saved!", { title: "Success" });
 */
toast.success = (content: ReactNode | string, options: Omit<ToastOptions, "type"> = {}): void => {
    toast(content, { ...options, type: "success" });
};

/**
 * Error toast - shorthand for toast with type "error"
 * 
 * @example
 * toast.error("An error occurred");
 * toast.error("Failed to save", { title: "Error" });
 */
toast.error = (content: ReactNode | string, options: Omit<ToastOptions, "type"> = {}): void => {
    toast(content, { ...options, type: "error" });
};

/**
 * Info toast - shorthand for toast with type "info"
 * 
 * @example
 * toast.info("Here is some information");
 * toast.info("Processing...", { title: "Info" });
 */
toast.info = (content: ReactNode | string, options: Omit<ToastOptions, "type"> = {}): void => {
    toast(content, { ...options, type: "info" });
};

/**
 * Warning toast - shorthand for toast with type "warning"
 * 
 * @example
 * toast.warning("This action cannot be undone");
 * toast.warning("Are you sure?", { title: "Warning" });
 */
toast.warning = (content: ReactNode | string, options: Omit<ToastOptions, "type"> = {}): void => {
    toast(content, { ...options, type: "warning" });
};

/**
 * Direct access to HeroUI's addToast for advanced usage
 * 
 * @example
 * heroToast({
 *   title: "Custom Toast",
 *   description: "With all HeroUI options",
 *   severity: "success",
 *   variant: "bordered",
 *   radius: "lg",
 *   timeout: 5000,
 *   shouldShowTimeoutProgress: true,
 * });
 */
export const heroToast = addToast;

/**
 * Helper for transaction confirmation toasts
 * 
 * @example
 * toastTxSuccess("0x123...", "Transaction confirmed!");
 */
export function toastTxSuccess(hash: string, message: string = "Transaction Confirmed!"): void {
    addToast({
        title: message,
        description: hash ? `Hash: ${hash.slice(0, 10)}...${hash.slice(-8)}` : undefined,
        severity: "success",
        timeout: 8000,
    });
}

/**
 * Helper for transaction error toasts
 * 
 * @example
 * toastTxError(error, "Transaction failed");
 */
export function toastTxError(error: Error | unknown, customMessage?: string): void {
    const errorMessage = error instanceof Error ? error.message : "Unknown error";
    addToast({
        title: customMessage || "Transaction Failed",
        description: errorMessage,
        severity: "danger",
        timeout: 10000,
    });
}

/**
 * Helper for promise-based toasts (loading, success, error states)
 * 
 * @example
 * toastPromise(
 *   fetchData(),
 *   {
 *     loading: "Loading data...",
 *     success: "Data loaded!",
 *     error: "Failed to load data"
 *   }
 * );
 */
export async function toastPromise<T>(
    promise: Promise<T>,
    messages: {
        loading?: string;
        success?: string | ((data: T) => string);
        error?: string | ((error: unknown) => string);
    }
): Promise<T> {
    // Show loading toast
    if (messages.loading) {
        toast.info(messages.loading);
    }

    try {
        const result = await promise;
    
        // Show success toast
        if (messages.success) {
            const successMessage = typeof messages.success === "function" 
                ? messages.success(result) 
                : messages.success;
            toast.success(successMessage);
        }
    
        return result;
    } catch (error) {
        // Show error toast
        if (messages.error) {
            const errorMessage = typeof messages.error === "function"
                ? messages.error(error)
                : messages.error;
            toast.error(errorMessage);
        }
    
        throw error;
    }
}

// Export default for named imports compatibility
export default toast;
